<template>
    <div class="l-stack l-gap-2 maintenance-form">
        <BaseMultiselect
            v-model="policy"
            :placeholder="$t('maintenancePolicy')"
            :options="maintenancePolicies"
            track-by="id"
            label="name"
            block
        />

        <BaseMultiselect
            v-model="asset"
            :placeholder="$t('shared.asset')"
            :allow-empty="false"
            :options="trackers"
            track-by="id"
            :custom-label="option => option.asset_details.name"
            block
        />

        <div class="l-inline l-gap-2">
            <DateInput v-model="date" :placeholder="$t('from')" block />

            <TimeInput v-model="time" block full />
        </div>

        <BaseInput
            v-for="condition in valueConditions"
            :key="condition.type"
            v-model.number="values[condition.type]"
            :disabled="isSubmitting"
            :placeholder="$t(condition.type)"
            type="number"
            block
        >
            <template #postfix>
                {{ units[condition.type] }}
            </template>
        </BaseInput>

        <RichTextInput
            v-model="notes"
            :disabled="isSubmitting"
            :placeholder="$t('notes')"
            block
        />

        <BaseCard class="maintenance-form__attachments">
            <IconButton @click="$refs.fileInput.click()">
                <AttachmentIcon />

                <input
                    ref="fileInput"
                    type="file"
                    multiple
                    @change="handleFileUpload"
                />
            </IconButton>

            <div class="l-stack">
                <div
                    v-for="(file, i) in files"
                    :key="i"
                    class="l-inline l-center-v l-gap-1"
                >
                    <FileIcon width="1em" height="1em" />

                    <span class="t-truncated">{{ file.name }}</span>

                    <IconButton
                        @click="
                            files = [
                                ...files.slice(0, i),
                                ...files.slice(i + 1),
                            ]
                        "
                    >
                        <RemoveIcon width="1em" height="1em" />
                    </IconButton>
                </div>
            </div>
        </BaseCard>

        <BaseButton
            :is-loading="isLoading"
            :disabled="isDisabled"
            type="submit"
            @click="handleSubmit"
        >
            {{ $t('shared.save') }}
        </BaseButton>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment-timezone'

import { httpHelper, measurementHelper } from '@/utils'
import AttachmentIcon from '@/components/icons/AttachmentIcon'
import BaseButton from '@/components/redesigned/BaseButton'
import BaseCard from '@/components/redesigned/BaseCard'
import BaseInput from '@/components/redesigned/BaseInput'
import BaseMultiselect from '@/components/redesigned/BaseMultiselect'
import DateInput from '@/components/redesigned/DateInput'
import FileIcon from '@/components/icons/FileIcon'
import IconButton from '@/components/IconButton'
import RemoveIcon from '@/components/icons/RemoveIcon'
import RichTextInput from '@/components/redesigned/RichTextInput'
import TimeInput from '@/components/redesigned/TimeInput'

const converters = {
    duration: value => value / 60 / 60 / 24,
    odometer: value => value / 1000,
    running_time: value => value / 60 / 60,
}

export default {
    name: 'MaintenanceForm',
    components: {
        AttachmentIcon,
        BaseButton,
        BaseCard,
        BaseInput,
        BaseMultiselect,
        DateInput,
        FileIcon,
        IconButton,
        RemoveIcon,
        RichTextInput,
        TimeInput,
    },
    props: {
        assetId: {
            type: Number,
            default: null,
        },
        policyId: {
            type: Number,
            default: null,
        },
    },
    data() {
        return {
            asset: null,
            assignment: null,
            date: null,
            files: [],
            isFetching: false,
            isSubmitting: false,
            notes: '',
            policy: null,
            time: '',
            units: {
                duration: this.$t('days'),
                odometer: measurementHelper.units.odometer,
                running_time: this.$t('hours'),
            },
            values: {},
        }
    },
    computed: {
        ...mapState('tracker', ['trackers']),
        ...mapState('maintenance', ['maintenancePolicies']),
        conditions() {
            return this.policy?.conditions || []
        },
        valueConditions() {
            return this.conditions.filter(c => c.type !== 'duration')
        },
        isDisabled() {
            return !(
                this.asset &&
                this.date &&
                this.notes.length &&
                this.time &&
                (!this.policy ||
                    this.valueConditions.every(
                        c => typeof this.values[c.type] === 'number'
                    ))
            )
        },
        isLoading() {
            return this.isFetching || this.isSubmitting
        },
    },
    watch: {
        asset() {
            this.fetchAssignment()
            this.populateValues()
        },
        policy() {
            this.fetchAssignment()
            this.populateValues()
        },
        isDisabled: {
            immediate: true,
            handler() {
                this.$emit('disabled', this.isDisabled)
            },
        },
        isLoading: {
            immediate: true,
            handler() {
                this.$emit('loading', this.isLoading)
            },
        },
    },
    mounted() {
        this.setCurrentTime()

        if (this.assetId) {
            this.asset = this.trackers.find(item => item.asset === this.assetId)
        }

        if (this.policyId) {
            this.policy = this.maintenancePolicies.find(
                item => item.id === this.policyId
            )
        }
    },
    methods: {
        populateValues() {
            const values = {}
            for (const condition of this.valueConditions) {
                const raw = this.asset?.asset_details.sensor_data[
                    condition.type
                ]?.value
                const converter = converters[condition.type]
                values[condition.type] =
                    typeof raw === 'number' && converter
                        ? Math.round(converter(raw))
                        : raw ?? null
            }
            this.values = values
        },
        async fetchAssignment() {
            this.assignment = null
            if (this.asset && this.policy) {
                this.isFetching = true
                const { data } = await httpHelper.get(
                    'maintenance-policy-assignments/',
                    {
                        params: {
                            asset: this.asset.asset,
                            policy: this.policy.id,
                        },
                    }
                )
                if (data.results?.length) {
                    this.assignment = data.results[0]
                }
                this.isFetching = false
            }
        },
        handleFileUpload(e) {
            if (e.target.files.length) {
                this.files.push(...e.target.files)
            }
        },
        async handleSubmit() {
            this.isSubmitting = true

            const timestamp = this.date.toJSON().slice(0, 11) + this.time

            const performedValues = {}
            for (const condition of this.valueConditions) {
                const converter = converters[condition.type]
                const val = this.values[condition.type]
                performedValues[condition.type] = converter
                    ? Math.round(val / converter(1))
                    : val
            }

            const { data } = await httpHelper.post(
                'maintenance-history-records/',
                {
                    asset: this.asset.asset,
                    notes: this.notes,
                    performed_values: performedValues,
                    policy: this.policy?.id,
                    timestamp,
                }
            )

            if (this.policy && this.assignment) {
                await httpHelper.patch(
                    `maintenance-policy-assignments/${this.assignment.id}/`,
                    {
                        last_performed: timestamp,
                        last_performed_values: performedValues,
                    }
                )
            } else if (this.policy) {
                await httpHelper.post('maintenance-policy-assignments/', {
                    asset: this.asset.asset,
                    policy: this.policy.id,
                    last_performed: timestamp,
                    last_performed_values: performedValues,
                })
            }

            if (data.documents_url && this.files.length) {
                await Promise.all(
                    this.files.map(async file => {
                        const formData = new FormData()
                        formData.append('file', file)
                        formData.append('history_record', data.id)
                        formData.append('name', file.name)
                        await httpHelper.post(data.documents_url, formData, {
                            headers: {
                                'Content-Type': 'multipart/form-data',
                            },
                        })
                    })
                )
            }

            this.setCurrentTime()
            this.asset = null
            this.files = []
            this.notes = ''
            this.policy = null
            this.values = {}
            this.isSubmitting = false
        },
        setCurrentTime() {
            this.date = moment
                .utc()
                .startOf('day')
                .toDate()
            this.time = moment().format('HH:mm')
        },
    },
}
</script>

<i18n>
{
    "en": {
        "days": "days",
        "duration": "Duration",
        "hours": "hours",
        "maintenancePolicy": "Maintenance policy",
        "notes": "Notes",
        "odometer": "Odometer",
        "running_time": "Running time"
    },
    "de": {
        "days": "Tage",
        "duration": "Laufzeit",
        "hours": "Stunden",
        "maintenancePolicy": "Wartungsplan",
        "notes": "Notizen",
        "odometer": "Kilometerstand",
        "running_time": "Betriebsstunden"
    },
    "fr": {
        "days": "jours",
        "duration": "Durée",
        "hours": "heures",
        "maintenancePolicy": "Politique de maintenance",
        "notes": "Notes",
        "odometer": "Kilométrage",
        "running_time": "Heures de fonctionnement"
    },
    "it": {
        "days": "giorni",
        "duration": "Durata",
        "hours": "ore",
        "maintenancePolicy": "Politica di manutenzione",
        "notes": "Note",
        "odometer": "Chilometraggio",
        "running_time": "Ore di funzionamento"
    }
}
</i18n>

<style lang="scss">
.maintenance-form {
    &__attachments {
        .icon-button {
            margin: 0.5rem;

            input {
                display: none;
            }
        }

        & > div {
            &:last-child {
                padding-left: 0.5rem;
            }

            svg {
                min-width: 1em;
            }
        }
    }
}
</style>
